import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Trash2, Paintbrush, Copy, RefreshCw } from 'lucide-react';
import { Asset, deleteAsset } from '@/utils/collectionsGraphQL';
import { EditAssetModal } from './EditAssetModal';
import { DeleteAssetDialog } from './DeleteAssetDialog';
import { AssetImageEditor } from './AssetImageEditor';
import { useToast } from '@/hooks/use-toast';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';

interface AssetTableProps {
  assets: Asset[];
  onDeleteSuccess?: () => void;
}

export const AssetTable: React.FC<AssetTableProps> = ({ 
  assets, 
  onDeleteSuccess
}) => {
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [deletingAsset, setDeletingAsset] = useState<Asset | null>(null);
  const [imageEditingAsset, setImageEditingAsset] = useState<Asset | null>(null);
  const [openDropdownAssetId, setOpenDropdownAssetId] = useState<string | null>(null);
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditSuccess = () => {
    setEditingAsset(null);
    if (onDeleteSuccess) onDeleteSuccess();
  };

  const handleImageEditSuccess = () => {
    setImageEditingAsset(null);
    if (onDeleteSuccess) onDeleteSuccess();
  };

  const handleDeleteClick = (asset: Asset) => {
    setDeletingAsset(asset);
  };

  const handleCopyImageUrl = async (asset: Asset) => {
    // Priority order: markup > cropped > original > legacy
    const imageUrl = asset.primary_markup_url || 
                     asset.primary_cropped_url || 
                     asset.primary_original_url || 
                     asset.primary_image;
                     
    if (!imageUrl) {
      toast({
        title: "No image available",
        description: "This asset doesn't have any image URL",
        variant: "destructive",
      });
      return;
    }

    const urlType = asset.primary_markup_url ? "markup" :
                    asset.primary_cropped_url ? "cropped" :
                    asset.primary_original_url ? "original" : "legacy";

    try {
      await navigator.clipboard.writeText(imageUrl);
      toast({
        title: "Success",
        description: `${urlType.charAt(0).toUpperCase() + urlType.slice(1)} image URL copied to clipboard`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy URL to clipboard",
        variant: "destructive",
      });
    }
  };


  const handleDeleteConfirm = async () => {
    if (!deletingAsset) return;
    
    try {
      await deleteAsset(deletingAsset.id);
      setDeletingAsset(null);
      if (onDeleteSuccess) onDeleteSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete asset",
        variant: "destructive",
      });
    }
  };

  const getAssetTypeLabel = (type: string) => {
    switch (type) {
      case 'simple':
        return 'Simple';
      case 'diff':
        return 'Diff';
      default:
        return type;
    }
  };

  const getAssetTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'diff':
        return 'default' as const;
      case 'simple':
        return 'secondary' as const;
      default:
        return 'outline' as const;
    }
  };

  const hasImages = (asset: Asset) => {
    return !!(asset.primary_image || asset.secondary_image);
  };

  const getImagesLabel = (asset: Asset) => {
    if (asset.primary_image && asset.secondary_image) return 'Both';
    if (asset.primary_image) return 'Primary';
    if (asset.secondary_image) return 'Secondary';
    return 'None';
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Page</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Images</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assets.map((asset) => (
              <TableRow key={asset.id}>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{asset.header || 'Untitled'}</div>
                    {asset.subheader && (
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {asset.subheader}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getAssetTypeBadgeVariant(asset.type)}>
                    {getAssetTypeLabel(asset.type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {asset.page?.subslug || 'N/A'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-mono">{asset.slug}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-mono">{asset.version}</div>
                </TableCell>
                <TableCell>
                  <div 
                    className={`text-sm ${hasImages(asset) ? 'text-blue-600 cursor-pointer hover:text-blue-700' : 'text-gray-600'}`}
                    onClick={() => hasImages(asset) && setImageEditingAsset(asset)}
                  >
                    {getImagesLabel(asset)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-600">
                    {formatDate(asset.created_at)}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu onOpenChange={(open) => setOpenDropdownAssetId(open ? asset.id : null)}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingAsset(asset);
                      }}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Details
                      </DropdownMenuItem>
                      {(asset.primary_image || asset.secondary_image) && (
                        <DropdownMenuItem onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setImageEditingAsset(asset);
                        }}>
                          <Paintbrush className="h-4 w-4 mr-2" />
                          Markup Image
                        </DropdownMenuItem>
                      )}
                      {(asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image) && (
                        <DropdownMenuItem onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCopyImageUrl(asset);
                        }}>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Image URL
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(asset);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Modal */}
      {editingAsset && (
        <EditAssetModal
          open={!!editingAsset}
          onOpenChange={(open) => !open && setEditingAsset(null)}
          asset={editingAsset}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Dialog */}
      {deletingAsset && (
        <DeleteAssetDialog
          open={!!deletingAsset}
          onOpenChange={(open) => !open && setDeletingAsset(null)}
          asset={deletingAsset}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Asset Image Editor */}
      {imageEditingAsset && (
        <AssetImageEditor
          open={!!imageEditingAsset}
          onOpenChange={(open) => !open && setImageEditingAsset(null)}
          asset={imageEditingAsset}
          onSuccess={handleImageEditSuccess}
        />
      )}
    </>
  );
};